import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

/**
 * Explicit-commit inline number editor.
 *
 *  - Typing changes the local value only.
 *  - Enter or the ✓ button commits (calls `onCommit(value)`).
 *  - Escape, Blur or the ✗ button cancels and reverts to `value`.
 *  - Save/cancel buttons appear only when the current input differs from `value`.
 *
 * The parent supplies the initial `value` and validates + persists inside
 * `onCommit`. The component simply guarantees no silent saves.
 */
export default function InlineNumberEditor({
  value,
  onCommit,
  step = "any",
  min,
  max,
  className = "",
  inputClassName = "db-inline w-20 text-right",
  buttonSize = 22,
  testid,
  disabled = false,
  // Optional formatter for the confirmed value (shown as tooltip / not required)
  ariaLabel,
}) {
  const [draft, setDraft] = useState(String(value ?? ""));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Reset draft when the committed value changes externally.
  useEffect(() => { setDraft(String(value ?? "")); }, [value]);

  const dirty = String(draft).trim() !== String(value ?? "").trim();

  const commit = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      // onCommit may be async and may throw; parent handles toasts.
      await onCommit(draft);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(String(value ?? "")); inputRef.current?.blur(); };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <input
        ref={inputRef}
        type="number"
        step={step}
        min={min}
        max={max}
        value={draft}
        disabled={disabled || saving}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={cancel}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        className={inputClassName}
        data-testid={testid}
      />
      {dirty && (
        <span className="inline-flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            title="Save (Enter)"
            aria-label="Save"
            disabled={saving}
            onMouseDown={(e) => { e.preventDefault(); commit(); }}
            className="grid place-items-center rounded border border-[#2D6A4F] bg-[#2D6A4F] text-white hover:bg-[#1B4332] disabled:opacity-50"
            style={{ width: buttonSize, height: buttonSize }}
            data-testid={testid ? `${testid}-save` : undefined}
          >
            <Check size={13} />
          </button>
          <button
            type="button"
            title="Cancel (Esc)"
            aria-label="Cancel"
            onMouseDown={(e) => { e.preventDefault(); cancel(); }}
            className="grid place-items-center rounded border border-[#E2E4E0] bg-white text-[#5C665A] hover:text-[#1A1D1A] hover:bg-[#F9F9F8]"
            style={{ width: buttonSize, height: buttonSize }}
            data-testid={testid ? `${testid}-cancel` : undefined}
          >
            <X size={13} />
          </button>
        </span>
      )}
    </span>
  );
}
